import {
  ApolloClient,
  ApolloProvider as ApolloProviderOriginal,
  from,
  HttpLink,
  split,
} from "@apollo/client";
import { ErrorLink, onError } from "@apollo/client/link/error";
import { getMainDefinition } from "@apollo/client/utilities";
import { Kind, OperationTypeNode } from "graphql";

import { SSELink } from "./apollo/SSELink";
import { CONFIG } from "../utils/config";
import { cache } from "./apollo/cache";

/**
 * Needed for any checks from the Apollo Client.
 */
const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Add the operation name to the request URL for easier request inspection.
 */
const fetchWithOperationName = (uri: string, options: { body: string }) => {
  try {
    const parsedBody = JSON.parse(options.body) as unknown;
    const operationName =
      isObject(parsedBody) && typeof parsedBody["operationName"] === "string"
        ? parsedBody["operationName"]
        : "";

    return fetch(`${uri}?op=${operationName}`, options);
  } catch (_error: unknown) {
    console.error(`Failed to parse body: ${options.body}`);
    return fetch(uri, options);
  }
};

/**
 * The HTTP link is used to specify the GraphQL endpoint.
 */
const httpLink = new HttpLink({
  uri: CONFIG.GRAPHQL_ENDPOINT,
  credentials: "include",
  fetch: fetchWithOperationName as typeof fetch,
});

const sseLink = new SSELink({
  url: CONFIG.GRAPHQL_ENDPOINT,
  credentials: "include",
  fetchFn: fetchWithOperationName as typeof fetch,
});

/**
 * Check whether there is a subscription operation in the query and use the
 * SSE link if there is. Fallback to the HTTP link otherwise.
 *
 * See https://www.apollographql.com/docs/react/api/link/introduction/#directional-composition
 */
const retryLink = split(
  (operation) => {
    const definition = getMainDefinition(operation.query);
    return (
      definition.kind === Kind.OPERATION_DEFINITION &&
      definition.operation === OperationTypeNode.SUBSCRIPTION
    );
  },
  sseLink,
  httpLink,
);

const errorHandler: ErrorLink.ErrorHandler = ({
  graphQLErrors,
  networkError,
}) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      const errorDetails = [
        `Message: ${message}`,
        `Location: ${JSON.stringify(locations)}`,
        `Path: ${path?.join(" > ") ?? "unknown"}`,
      ].join(", ");
      console.error(`GraphQL error: ${errorDetails}`);
    });
  }

  if (networkError) {
    const errorMessage = `[GraphQL network error]: ${networkError.toString()}`;
    console.error(errorMessage);
  }
};

/**
 * The error link is used to handle errors from the GraphQL server.
 */
const errorLink = onError(errorHandler);

const client = () =>
  new ApolloClient({
    connectToDevTools: true,
    cache,
    link: from([errorLink, retryLink]),
  });

export const ApolloProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  return (
    <ApolloProviderOriginal client={client()}>
      {children}
    </ApolloProviderOriginal>
  );
};
